import React, { useEffect, useRef } from "react";
import TitlePages from "../../layout/TitlePages";
import "./PrincipiosRectores.css";

/**
 * Principios Rectores del Instituto Tlaxcalteca de Elecciones (ITE)
 * Rediseño editorial elegante sin cards, preservando íntegramente
 * los textos originales.
 */
const PRINCIPIOS_DATA = [
  {
    name: "Constitucionalidad",
    text: "Referido al apego escrupuloso que mantiene el Instituto en el mandato constitucional para el ejercicio de sus atribuciones y el desempeño de las funciones encomendadas.",
  },
  {
    name: "Legalidad",
    text: "El Instituto Tlaxcalteca de Elecciones debe observar escrupulosamente el mandato constitucional en el ejercicio de las atribuciones y el desempeño de las funciones que tiene encomendadas, así como las disposiciones legales que lo reglamentan.",
  },
  {
    name: "Certeza",
    text: "Todas las acciones que desempeñe el Instituto Tlaxcalteca de Elecciones estarán dotadas de veracidad, certidumbre y apego a los hechos, esto es, que los resultados de sus actividades sean completamente verificables, fidedignos y confiables.",
  },
  {
    name: "Autonomía",
    text: "Se establece como la facultad que le otorga la ley al Instituto Tlaxcalteca de Elecciones, que le permite actuar, hacer o decidir en la deliberación de sus procesos y desarrollo de sus actividades en apego a las normas y leyes que lo reglamentan.",
  },
  {
    name: "Independencia",
    text: "Es la garantía que disponen las autoridades que conforman la Institución para que sus procesos de deliberación y toma de decisiones se den con absoluta libertad y respondan única y exclusivamente al imperio de la ley.Es la garantía que disponen las autoridades que conforman la Institución para que sus procesos de deliberación y toma de decisiones se den con absoluta libertad y respondan única y exclusivamente al imperio de la ley.",
  },
  {
    name: "Imparcialidad",
    text: "En el desarrollo de sus actividades, el Instituto Tlaxcalteca de Elecciones debe reconocer y velar permanentemente por el interés de la sociedad y por los valores fundamentales de la democracia, supeditando a éstos, de manera irrestricta, cualquier interés personal o preferencia política.",
  },
  {
    name: "Equidad",
    text: "Es considerada como un instrumento del derecho que permite la aplicación de las disposiciones legales a casos concretos, en un estricto sentido de justicia e imparcialidad.",
  },
  {
    name: "Objetividad",
    text: "La objetividad implica al Instituto Tlaxcalteca de Elecciones un quehacer institucional fundado en el reconocimiento coherente y razonado de la realidad sobre la que se actúa y la obligación de percibir e interpretar los hechos por encima de visiones y opiniones, parciales o unilaterales, máxime si éstas pueden alterar la expresión o consecuencia del quehacer del Instituto.",
  },
  {
    name: "Profesionalismo",
    text: "Se establece como una condición que mantiene el Instituto Tlaxcalteca de elecciones en el desarrollo de sus actividades en base a la eficacia, aplicación, seriedad, honradez y pericia.",
  },
  {
    name: "Máxima Publicidad",
    text: "Todos los actos y la información en poder del Instituto Tlaxcalteca de Elecciones son públicos y sólo por excepción se podrán reservar en los casos expresamente previstos por las leyes y justificados bajo determinadas circunstancias.",
  },
  {
    name: "Paridad",
    text: "La paridad es un principio constitucional que tiene como finalidad la igualdad sustantiva entre los sexos, que adopta nuestro país como parte de los compromisos internacionales que ha adquirido con el objeto de que los derechos político electorales de las y los ciudadanos se ejerzan en condiciones de igualdad.",
  },
];

const PrincipiosRectores = () => {
  const itemRefs = useRef([]);

  useEffect(() => {
    document.title = "Principios Rectores";

    let fallbackTimer;
    let observer;

    try {
      // Activar clase js-ready para mejora progresiva
      document.documentElement.classList.add("js-ready");

      // Respaldo de seguridad a los 2 segundos para asegurar visibilidad
      fallbackTimer = setTimeout(() => {
        itemRefs.current.forEach((el) => {
          if (el) el.classList.add("is-visible");
        });
      }, 2000);

      // IntersectionObserver con activación única
      if ("IntersectionObserver" in window) {
        observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                entry.target.classList.add("is-visible");
                observer.unobserve(entry.target);
              }
            });
          },
          {
            rootMargin: "0px 0px -40px 0px",
            threshold: 0.1,
          }
        );

        itemRefs.current.forEach((el) => {
          if (el) observer.observe(el);
        });
      } else {
        // Fallback inmediato si no hay soporte de IntersectionObserver
        itemRefs.current.forEach((el) => {
          if (el) el.classList.add("is-visible");
        });
      }
    } catch (err) {
      // En caso de cualquier error, asegurar contenido visible
      itemRefs.current.forEach((el) => {
        if (el) el.classList.add("is-visible");
      });
    }

    return () => {
      if (fallbackTimer) clearTimeout(fallbackTimer);
      if (observer) observer.disconnect();
    };
  }, []);

  return (
    <>
      <TitlePages title="Principios Rectores" subTitle="" />

      <article className="principles-page" lang="es">
        <header className="principles-intro">
          <div className="principles-intro__columns">
            <p>
              La Constitución Política del Estado de Tlaxcala en su artículo 95 señala
              que el Instituto Tlaxcalteca de Elecciones, cumplirá sus funciones bajo
              los principios de constitucionalidad, legalidad, imparcialidad,
              objetividad, equidad, certeza, profesionalismo, independencia y máxima
              publicidad.
            </p>
            <p>
              De la misma manera, el artículo 2 de la Ley de Instituciones y
              Procedimientos para el Estado de Tlaxcala establece como principios
              rectores en el ejercicio de la función electoral, la constitucionalidad,
              legalidad, certeza, autonomía, independencia, imparcialidad, equidad,
              objetividad, profesionalismo y máxima publicidad
            </p>
          </div>
          <div className="principles-accent-line" aria-hidden="true" />
        </header>

        <ol className="principles">
          {PRINCIPIOS_DATA.map((principio, index) => (
            <li
              key={principio.name}
              className="principles__item"
              ref={(el) => (itemRefs.current[index] = el)}
              tabIndex={0}
            >
              <span className="principles__num" aria-hidden="true" />
              <h3 className="principles__name">{principio.name}</h3>
              <p className="principles__text">{principio.text}</p>
            </li>
          ))}
        </ol>
      </article>
    </>
  );
};

export default PrincipiosRectores;
